import { Box as MuiBox, InputBase } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import {
    CATEGORY_LABELS,
    formatShortcutKey,
    SHORTCUT_DEFS,
    type ShortcutCategory,
    type ShortcutDef,
    shortcutMatchesQuery,
    visibleShortcutDefs,
} from "config/shortcuts";
import { selectObjectMeasureEnabled } from "slices/explorer";
import { isMacintosh } from "utils/misc";

const CATEGORY_ORDER: ShortcutCategory[] = [
    "navigation",
    "tools",
    "measure-mode",
    "section-mode",
    "view",
    "panels",
    "objects",
    "edit",
    "actions",
];

const BORDER_TABLE = "1px solid var(--color-border-default, #acb5b9)";
const BORDER_ROW = "1px solid var(--color-border-dataTable, #d6dadc)";

const cellTextSx = {
    fontSize: "0.75rem",
    color: "var(--color-text-primary, #232729)",
    letterSpacing: "0.25px",
    lineHeight: "16px",
} as const;

function getShortcutDisplay(shortcut: ShortcutDef, isMac: boolean): string {
    if (isMac && shortcut.macDisplayOverride) {
        return shortcut.macDisplayOverride;
    }
    if (shortcut.displayOverride) {
        return isMac ? shortcut.displayOverride.replace(/\bAlt\b/g, "Opt") : shortcut.displayOverride;
    }
    return formatShortcutKey(shortcut.defaultKey, isMac);
}

export function ShortcutsTab() {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const objectMeasureEnabled = useAppSelector(selectObjectMeasureEnabled);
    const visibleDefs = visibleShortcutDefs(SHORTCUT_DEFS, { objectMeasure: objectMeasureEnabled });

    const grouped = CATEGORY_ORDER.map((cat) => {
        const categoryLabel = t(CATEGORY_LABELS[cat]);
        return {
            category: cat,
            categoryLabel,
            shortcuts: visibleDefs.filter(
                (s) =>
                    s.category === cat &&
                    shortcutMatchesQuery(
                        t(`settingsShortcuts.labels.${s.id}`),
                        categoryLabel,
                        getShortcutDisplay(s, isMacintosh),
                        query,
                    ),
            ),
        };
    }).filter((g) => g.shortcuts.length > 0);

    return (
        // flex:1 + minHeight:0 fills remaining height after the tabs bar; content scrolls inside
        <MuiBox display="flex" flexDirection="column" flex={1} minHeight={0} bgcolor="#fff">
            {/* Search — fixed in flex layout, never scrolls */}
            <MuiBox
                px="16px"
                py="12px"
                bgcolor="#fff"
                borderBottom="1px solid var(--color-border-separator, #d6dadc)"
                flexShrink={0}
                position="relative"
                zIndex={1}
            >
                <MuiBox
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    bgcolor="#eef0f1"
                    borderRadius="4px"
                    pl="12px"
                    pr="8px"
                    height="28px"
                >
                    <InputBase
                        placeholder={t("settingsShortcuts.filterPlaceholder")}
                        inputProps={{ "aria-label": t("settingsShortcuts.filterPlaceholder") }}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{
                            flex: 1,
                            fontSize: "0.875rem",
                            letterSpacing: "0.15px",
                            color: "#6a767c",
                            "& .MuiInputBase-input": {
                                p: 0,
                                color: "#6a767c",
                                "&::placeholder": { color: "#6a767c", opacity: 1 },
                            },
                        }}
                    />
                    {/* Clear button — only visible when query is non-empty */}
                    {query && (
                        <MuiBox
                            component="button"
                            onClick={() => setQuery("")}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            sx={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                color: "#6a767c",
                                lineHeight: 0,
                                "&:hover": { color: "#232729" },
                            }}
                            aria-label={t("settingsShortcuts.clearSearch")}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 4L4 12M4 4L12 12"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </MuiBox>
                    )}
                    {/* Search icon */}
                    <MuiBox display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.8371 9.42277C11.3606 8.63227 11.6655 7.68405 11.6655 6.66541C11.6655 3.90398 9.42695 1.66541 6.66553 1.66541C3.9041 1.66541 1.66553 3.90398 1.66553 6.66541C1.66553 9.42683 3.9041 11.6654 6.66553 11.6654C7.68417 11.6654 8.63239 11.3605 9.42289 10.837L13.0587 14.4728L14.4729 13.0586L10.8371 9.42277ZM6.66553 9.66541C8.32238 9.66541 9.66553 8.32226 9.66553 6.66541C9.66553 5.00855 8.32238 3.66541 6.66553 3.66541C5.00867 3.66541 3.66553 5.00855 3.66553 6.66541C3.66553 8.32226 5.00867 9.66541 6.66553 9.66541Z"
                                fill="#232729"
                            />
                        </svg>
                    </MuiBox>
                </MuiBox>
            </MuiBox>

            {/* Scrollable table area — flex:1 + overflow:auto contains all scroll */}
            <MuiBox
                flex={1}
                overflow="auto"
                bgcolor="#fff"
                px="16px"
                py="16px"
                display="flex"
                flexDirection="column"
                gap="16px"
            >
                {query.trim() && grouped.length === 0 && (
                    <MuiBox
                        role="status"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                        color="var(--color-text-secondary, #5e696e)"
                        fontSize="0.875rem"
                    >
                        {t("settingsShortcuts.noResults")}
                    </MuiBox>
                )}
                {grouped.map(({ category, categoryLabel, shortcuts }) => (
                    // flexShrink:0 prevents flex from squishing tables to fit
                    <MuiBox key={category} border={BORDER_TABLE} borderRadius="6px" overflow="hidden" flexShrink={0}>
                        {/* Section header row */}
                        <MuiBox
                            display="flex"
                            bgcolor="var(--color-background-dataTable-header, #f4f5f6)"
                            borderBottom={BORDER_ROW}
                        >
                            <MuiBox flex={1} px="16px" py="12px" sx={cellTextSx} fontWeight={600}>
                                {categoryLabel}
                            </MuiBox>
                            <MuiBox flex={1} px="16px" py="12px" sx={cellTextSx} fontWeight={600}>
                                {t("settingsShortcuts.shortcutColumn")}
                            </MuiBox>
                        </MuiBox>

                        {/* Data rows */}
                        {shortcuts.map((shortcut, idx) => {
                            const isLast = idx === shortcuts.length - 1;
                            const display = getShortcutDisplay(shortcut, isMacintosh);
                            return (
                                <MuiBox
                                    key={shortcut.id}
                                    display="flex"
                                    bgcolor="#fff"
                                    borderBottom={isLast ? "none" : BORDER_ROW}
                                    sx={{ "&:hover": { bgcolor: "var(--color-background-secondary, #f4f5f6)" } }}
                                >
                                    <MuiBox flex={1} px="16px" py="8px" sx={cellTextSx}>
                                        {t(`settingsShortcuts.labels.${shortcut.id}`)}
                                    </MuiBox>
                                    <MuiBox flex={1} px="16px" py="8px" sx={cellTextSx}>
                                        {display}
                                    </MuiBox>
                                </MuiBox>
                            );
                        })}
                    </MuiBox>
                ))}
            </MuiBox>
        </MuiBox>
    );
}
